import React, { useContext, useState, useEffect, useCallback, type JSX } from 'react';
import { useLazyQuery, QueryLazyOptions, useQuery } from '@apollo/client';
import kontoplanQuery from './KonteringManagerQuery.gql';
import settingsQuery from './KonteringManagerSettingsQuery.gql';
import {
  KonteringManagerQueryVariables as IKontoplanVariables,
  KonteringManagerQuery as IKontoplanData,
  KonteringManagerQuery_rtn_EKKodplan,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Kombinationer_All,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Kombinationsvillkor_All,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Tillaggsinfo,
  KonteringManagerQuery_db_EKTillaggsinfo_All,
  KonteringManagerQuery_db_EKTillaggsinfo_All__Associations_Falt_All,
  KonteringManagerQuery_db_EKKontodelkombination_All,
  KonteringManagerQuery_db_EKKonteringsvarde_All,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_LokalKontoanvandning_All,
  KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Enhet,
  KonteringManagerQuery_db_SYSBokfar_All,
} from './_QueryTypes/KonteringManagerQuery';
import {
  TEDEKKontodelkombinationKOMBINATIONEnum,
  TEDEKKontodeldefVERKSAMHETEnum,
  TEDEKKontodeldefPROJEKTEnum,
  TEDEKKontodeldefSTIFTELSEEnum,
  TEDEKKontodeldefANVANDS_FOREnum,
  TEDEKKontodelREDOVISN_TYPEnum,
} from '../../../../../../globalQueryTypes';
import {
  KonteringManagerSettingsQueryVariables as ISettingsVariables,
  KonteringManagerSettingsQuery as ISettingsData,
} from './_QueryTypes/KonteringManagerSettingsQuery';
import Logger, { namedError } from '../../../../../../Utils/Logger';
import { convertUrval } from '../../../../../../Components/Urval/Components/SavedUrval/Utils/convertSavedUrval';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { EK_KONTO_URVAL_COLLECTION_NAME } from '../../../../../../Components/Urval/Consts/UrvalConsts';
import { KodplanContext } from '../../../../../../Components/KodplanManager/Context/KodplanContext';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IAttestKodbehorighetUrval } from '../KonteringContext';

interface IProps {
  children: (args: IChildrenArgs) => JSX.Element;
}

export type getKontoplanFnType = (
  options?: QueryLazyOptions<IKontoplanVariables> | undefined
) => void;

interface IChildrenArgs {
  loading: boolean;
  kontoplaner: IKontoplan[];
  attestKodbehUrval: IAttestKodbehorighetUrval[];
  bokforingsAr: KonteringManagerQuery_db_SYSBokfar_All[];
  getKontoplan: getKontoplanFnType;
  konteringsvarden: KonteringManagerQuery_db_EKKonteringsvarde_All[];
  kontodelkombinationer: KonteringManagerQuery_db_EKKontodelkombination_All[];
}

export enum KontodelsDefUndertyp {
  PROJEKT,
  VERKSAMHET,
  STIFTELSE,
  OMRADE,
  GAVA,
  OBJEKT,
}

// export interface IKontoplaner {
//   [kontoPlanId: string]: IKontoplan;
// }

export interface IKontoplan {
  id: string;
  namn: string;
  franBokfAr: Date;
  tillBokfAr: Date;
  /** K=Kyrkbas;S=Stiftelse;E=EUBas;T=Stift */
  typ: string;
  kontodelDefs: IKontodelDef[];
  dataReadTime: Date | null;
}

export interface IKontodelDef {
  id: string;
  namn: string;
  ordningsNr: number;
  antalPositioner: number;
  typ: KontodelsDefUndertyp | null;
  kontodelar: IKontodel[];
  visibleFromDate: Date;
  visibleToDate: Date;
  anvandsForMoms: boolean | null;
}

export interface IKontodel {
  id: string;
  aktiv: boolean;
  antalsRedovisning: boolean;
  benamning: string;
  budgetKonto: boolean;
  enhet:
    | KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Enhet
    | null
    | undefined;
  franDatum: Date;
  internKonto: boolean;
  kontodel: string;
  kombinationer: IKontodelKombination[];
  kombinationsVillkor: IKontodelKombinationsVillkor[];
  localUsage:
    | KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_LokalKontoanvandning_All[]
    | null
    | undefined;
  ordningsNr: number;
  redovisningsTyp: TEDEKKontodelREDOVISN_TYPEnum;
  rubrikKonto: boolean;
  sparraManuell: boolean;
  sparrAttest: boolean;
  tillDatum: Date;
  tillaggsinfo: IKontodelTillaggsinfo | null;
  undantagPeriodisering: boolean | null;
  nuvMomspliktFran?: any | null;
  nuvMomspliktTill?: any | null;
  nuvMomspliktProcent?: number | null;
  foregMomspliktFran?: any | null;
  foregMomspliktTill?: any | null;
  foregMomspliktProcent?: number | null;
  foregForegMomspliktFran?: any | null;
  foregForegMomspliktTill?: any | null;
  foregForegMomspliktProcent?: number | null;
}

export interface IKontodelKombination {
  kombOrdningsNr: number;
  kombination: TEDEKKontodelkombinationKOMBINATIONEnum;
  lokaltKrav: boolean;
}

export interface IKontodelKombinationsVillkor {
  kombOrdningsNr: number;
  fromKontodel: string;
  toKontodel: string;
}

export interface IKontodelTillaggsinfo {
  benamning: string;
  aktiv: boolean;
  kod: string;
  falt: ITillaggsinfoFalt[];
}

export interface ITillaggsinfoFalt {
  benamning: string;
  aktiv: boolean;
  sortorder: number | null;
  fieldName: string;
  obligatorisk: boolean;
  beskrivning: string | null;
}

const buildKontoPlan = (
  rawDataKontoplan: KonteringManagerQuery_rtn_EKKodplan,
  rawTillaggsinfo: KonteringManagerQuery_db_EKTillaggsinfo_All[] | null
): IKontoplan => {
  return {
    id: rawDataKontoplan.id,
    namn: rawDataKontoplan.NAMN,
    franBokfAr: new Date(rawDataKontoplan.FRAN_BOKF_AR),
    tillBokfAr: new Date(rawDataKontoplan.TILL_BOKF_AR),
    typ: rawDataKontoplan.TYP ?? 'K',
    dataReadTime: new Date(),
    kontodelDefs:
      rawDataKontoplan._Associations?.Kontodelsdef_All?.map(
        buildKontodelsDef(rawTillaggsinfo)
      ) ?? [],
  };
};

const buildKontodelsDef =
  (rawTillaggsinfo: KonteringManagerQuery_db_EKTillaggsinfo_All[] | null) =>
  (
    rawKontodelDef: KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All
  ): IKontodelDef => {
    // Kolla om vi behöver kolla på specifik enum
    const undertyp =
      rawKontodelDef.VERKSAMHET === TEDEKKontodeldefVERKSAMHETEnum.JA
        ? KontodelsDefUndertyp.VERKSAMHET
        : rawKontodelDef.PROJEKT === TEDEKKontodeldefPROJEKTEnum.JA
        ? KontodelsDefUndertyp.PROJEKT
        : rawKontodelDef.STIFTELSE === TEDEKKontodeldefSTIFTELSEEnum.JA
        ? KontodelsDefUndertyp.STIFTELSE
        : rawKontodelDef.ANVANDS_FOR ===
          TEDEKKontodeldefANVANDS_FOREnum.ANSVARSOMRADE
        ? KontodelsDefUndertyp.OMRADE
        : rawKontodelDef.ANVANDS_FOR === TEDEKKontodeldefANVANDS_FOREnum.GAVOR
        ? KontodelsDefUndertyp.GAVA
        : rawKontodelDef.ANVANDS_FOR === TEDEKKontodeldefANVANDS_FOREnum.OBJEKT
        ? KontodelsDefUndertyp.OBJEKT
        : null;

    return {
      id: rawKontodelDef.id,
      namn: rawKontodelDef.NAMN,
      ordningsNr: rawKontodelDef.ORDNINGS_NR,
      antalPositioner: rawKontodelDef.ANTAL_POS,
      typ: undertyp,
      visibleFromDate: new Date(rawKontodelDef.VISIBLEFROMDATE),
      visibleToDate: new Date(rawKontodelDef.VISIBLETODATE),
      kontodelar:
        rawKontodelDef._Associations?.Kontodel_All?.map(
          buildKontodel(rawTillaggsinfo)
        ) ?? [],
      anvandsForMoms: rawKontodelDef.anvandForMoms,
    };
  };

const buildKontodel =
  (rawTillaggsinfo: KonteringManagerQuery_db_EKTillaggsinfo_All[] | null) =>
  (
    rawKontodel: KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All
  ): IKontodel => {
    return {
      aktiv: rawKontodel.AKTIV ?? false,
      benamning: rawKontodel.BENAMNING ?? '',
      id: rawKontodel.id,
      kontodel: rawKontodel.KONTODEL,
      ordningsNr: rawKontodel.ORDNINGS_NR,
      sparraManuell: rawKontodel.SPARRA_MANUELL ?? false,
      sparrAttest: rawKontodel.SPARR_ATTEST ?? false,
      internKonto: rawKontodel.INTERN_KONTO ?? false,
      budgetKonto: rawKontodel.BUDGETKONTO,
      antalsRedovisning: rawKontodel.ANTALSREDOVISNING ?? false,
      franDatum: new Date(rawKontodel.FRAN_DATUM),
      tillDatum: new Date(rawKontodel.TILL_DATUM),
      redovisningsTyp: rawKontodel.REDOVISN_TYP,
      rubrikKonto: rawKontodel.RUBRIKKONTO ?? false,
      kombinationer: buildKombinationer(
        rawKontodel._Associations?.Kombinationer_All ?? []
      ),
      kombinationsVillkor: buildKombinationsVillkor(
        rawKontodel._Associations?.Kombinationsvillkor_All ?? []
      ),
      tillaggsinfo: buildTillaggsinfo(
        rawKontodel._Associations?.Tillaggsinfo ?? null,
        rawTillaggsinfo,
        rawKontodel.TILLAGGSINFO_KOD ?? ''
      ),
      localUsage: rawKontodel._Associations?.LokalKontoanvandning_All,
      enhet: rawKontodel._Associations?.Enhet,
      undantagPeriodisering: rawKontodel.UNDANTAG_PERIODISERING ?? false,
      nuvMomspliktFran: rawKontodel.Nuv_Momsplikt_Fran,
      nuvMomspliktTill: rawKontodel.Nuv_Momsplikt_Till,
      nuvMomspliktProcent: rawKontodel.Nuv_Momsplikt_Procent,
      foregMomspliktFran: rawKontodel.Foreg_Momsplikt_Fran,
      foregMomspliktTill: rawKontodel.Foreg_Momsplikt_Till,
      foregMomspliktProcent: rawKontodel.Foreg_Momsplikt_Procent,
      foregForegMomspliktFran: rawKontodel.Foreg_Foreg_Momsplikt_Fran,
      foregForegMomspliktTill: rawKontodel.Foreg_Foreg_Momsplikt_Till,
      foregForegMomspliktProcent: rawKontodel.Foreg_Foreg_Momsplikt_Procent,
    };
  };

const buildKombinationer = (
  rawKombinationer: KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Kombinationer_All[]
): IKontodelKombination[] => {
  return rawKombinationer.map(rawKombination => ({
    kombOrdningsNr: rawKombination.KOMB_ORDNINGS_NR,
    kombination: rawKombination.KOMBINATION,
    lokaltKrav: rawKombination.LOKALT_KRAV ?? false,
  }));
};

const buildKombinationsVillkor = (
  rawKombinationsVillkor: KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Kombinationsvillkor_All[]
): IKontodelKombinationsVillkor[] => {
  return rawKombinationsVillkor.map(rawVillkor => ({
    kombOrdningsNr: rawVillkor.KOMB_ORDNINGS_NR,
    fromKontodel: rawVillkor.FROM_KONTODEL ?? '',
    toKontodel: rawVillkor.TOM_KONTODEL ?? '',
  }));
};

const buildTillaggsinfo = (
  rawTillaggsinfoShort: KonteringManagerQuery_rtn_EKKodplan__Associations_Kontodelsdef_All__Associations_Kontodel_All__Associations_Tillaggsinfo | null,
  rawTillaggsinfoAll: KonteringManagerQuery_db_EKTillaggsinfo_All[] | null,
  tillaggsinfokod: string
): IKontodelTillaggsinfo | null => {
  if (!rawTillaggsinfoShort || !rawTillaggsinfoAll) {
    return null;
  }

  const rawTillaggsinfo = rawTillaggsinfoAll.find(
    tillaggsinfo => tillaggsinfo.id === rawTillaggsinfoShort.id
  );

  if (!rawTillaggsinfo) {
    return null;
  }

  return {
    benamning: rawTillaggsinfo.BENAMNING,
    aktiv: Boolean(rawTillaggsinfo.AKTIV),
    kod: tillaggsinfokod,
    falt:
      rawTillaggsinfo._Associations?.Falt_All?.map(buildTillaggsinfoFalt) ?? [],
  };
};

const buildTillaggsinfoFalt = (
  rawTillaggsinfoFalt: KonteringManagerQuery_db_EKTillaggsinfo_All__Associations_Falt_All
): ITillaggsinfoFalt => {
  return {
    aktiv: Boolean(rawTillaggsinfoFalt.AKTIV),
    benamning: rawTillaggsinfoFalt.BENAMNING,
    beskrivning: rawTillaggsinfoFalt.BESKRIVNING,
    fieldName: rawTillaggsinfoFalt.FIELD_NAME,
    obligatorisk: Boolean(rawTillaggsinfoFalt.OBLIGATORISK),
    sortorder: rawTillaggsinfoFalt.SORTORDER,
  };
};

const KonteringManagerQuery: React.FC<IProps> = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const kodplanContext = useContext(KodplanContext);
  const [kontoplaner, setKontoplaner] = useState(
    kodplanContext.availableKodplanList.map(
      (kodplan): IKontoplan => ({
        id: kodplan.id,
        dataReadTime: null,
        namn: '',
        franBokfAr: new Date(),
        tillBokfAr: new Date(),
        typ: 'K',
        kontodelDefs: [],
      })
    )
  );
  const { avenyCollectionMap } = useContext(ModelContext);

  useEffect(() => {
    setKontoplaner(
      kodplanContext.availableKodplanList.map(
        (kodplan): IKontoplan => ({
          id: kodplan.id,
          dataReadTime: null,
          namn: '',
          franBokfAr: new Date(),
          tillBokfAr: new Date(),
          typ: 'K',
          kontodelDefs: [],
        })
      )
    );
  }, [kodplanContext.availableKodplanList]);

  const [getKontoplanOrigin, queryResultKontoplan] = useLazyQuery<
    IKontoplanData,
    IKontoplanVariables
  >(kontoplanQuery, {});

  const getKontoplan = useCallback(
    (options?: QueryLazyOptions<IKontoplanVariables> | undefined) => {
      if (
        !queryResultKontoplan.loading &&
        options &&
        options.variables &&
        options.variables?.kodplanId !== '' &&
        options.variables?.kodplanId != undefined
      ) {
        getKontoplanOrigin(options);
      }
    },
    [getKontoplanOrigin, queryResultKontoplan.loading]
  );

  const queryResultSettings = useQuery<ISettingsData, ISettingsVariables>(
    settingsQuery,
    {
      variables: {
        dbname: activeDatabase ?? '',
      },
      skip: !activeDatabase,
    }
  );

  if (queryResultKontoplan.loading || queryResultSettings.loading) {
    return props.children({
      loading: queryResultKontoplan.loading || queryResultSettings.loading,
      attestKodbehUrval: [],
      bokforingsAr: [],
      kontoplaner,
      getKontoplan: getKontoplan,
      konteringsvarden: [],
      kontodelkombinationer: [],
    });
  } else if (
    queryResultKontoplan.error &&
    !queryResultKontoplan.error.networkError
  ) {
    Logger.warn(
      namedError(
        'KonteringManagerQuery-HämtaKontodelar',
        'KonteringValidationManagerQuery: Error vid hämtning av kontodelar, ' +
          queryResultKontoplan.error.message,
        queryResultKontoplan.error
      )
    );
  } else if (
    queryResultSettings.error &&
    !queryResultSettings.error.networkError
  ) {
    Logger.warn(
      namedError(
        'KonteringManagerQuery-HämtaInställningar',
        'KonteringValidationManagerQuery: Error vid hämtning av inställningar, ' +
          queryResultSettings.error?.message,
        queryResultSettings.error
      )
    );
  } else if (queryResultKontoplan.data) {
    const attestKodbehUrval: IAttestKodbehorighetUrval[] =
      queryResultSettings.data?.me?.[0]?.attestKodbehorigheter?.map(kodbeh => ({
        urval: convertUrval(
          kodbeh?.attestKodbehorighet,
          EK_KONTO_URVAL_COLLECTION_NAME,
          avenyCollectionMap
        ),
        omradesId: kodbeh?.omradeID,
        kodplanID: kodbeh?.kodplanID,
        attestRattID: kodbeh?.attestRattID,
        attestTyp: kodbeh?.attestTyp,
        maxbelopp: kodbeh?.maxbelopp,
        hasKontrollAttestant: kodbeh?.hasKontrollAttestant,
        kontrollbelopp: kodbeh?.kontrollbelopp,
      })) ?? [];

    const attestKodbehUrvalUpdated = attestKodbehUrval.reduce<
      IAttestKodbehorighetUrval[]
    >(
      (prev, selection) => {
        if (selection.urval.pages.length > 1) {
          let newSelections: IAttestKodbehorighetUrval[] = [];
          selection.urval.pages.forEach((page, index) => {
            if (selection.urval.savedUrvalProps) {
              newSelections.push({
                ...selection,
                urval: {
                  savedUrvalProps: {
                    ...selection.urval.savedUrvalProps,
                    namn: selection.urval.savedUrvalProps.namn
                      ? selection.urval.savedUrvalProps.namn +
                        ` (sida ${index + 1})`
                      : `Sida ${index + 1}`,
                  },
                  pages: [page],
                },
              });
            } else {
              newSelections.push({
                ...selection,
                urval: {
                  pages: [page],
                },
              });
            }
          });
          return [...prev, ...newSelections];
        } else {
          return [...prev, selection];
        }
      },
      []
    );
    if (
      queryResultKontoplan.data.rtn?.EKKodplan?.__typename === 'TEDEKKontoplan'
    ) {
      const kontoplan = buildKontoPlan(
        queryResultKontoplan.data.rtn.EKKodplan,
        queryResultKontoplan.data.db?.EKTillaggsinfo_All ?? null
      );

      if (
        kontoplaner.find(kp => kp.id === kontoplan.id)?.dataReadTime === null
      ) {
        setKontoplaner(prevKontoplaner => {
          const otherKontoplaner = prevKontoplaner.filter(
            kplan => kplan.id !== kontoplan.id
          );
          return [...otherKontoplaner, kontoplan];
        });
      }
    }

    return props.children({
      attestKodbehUrval: attestKodbehUrvalUpdated,
      bokforingsAr: queryResultKontoplan.data.db?.SYSBokfar_All ?? [],
      getKontoplan: getKontoplan,
      kontoplaner,
      konteringsvarden:
        queryResultKontoplan.data.db?.EKKonteringsvarde_All ?? [],
      kontodelkombinationer:
        queryResultKontoplan.data.db?.EKKontodelkombination_All ?? [],
      loading: queryResultKontoplan.loading || queryResultSettings.loading,
    });
  }

  return props.children({
    kontoplaner: kontoplaner,
    attestKodbehUrval: [],
    bokforingsAr: [],
    getKontoplan: getKontoplan,
    konteringsvarden: [],
    kontodelkombinationer: [],
    loading: queryResultKontoplan.loading || queryResultSettings.loading,
  });
};

export default KonteringManagerQuery;
